import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Button, Card, CardBody, CardHeader, Col } from "reactstrap";

/* ---------------- UTILS ---------------- */

const isToday = (dateStr) => {
  const today = new Date();
  const selected = new Date(dateStr);
  return (
    today.getFullYear() === selected.getFullYear() &&
    today.getMonth() === selected.getMonth() &&
    today.getDate() === selected.getDate()
  );
};

const getCurrentShift = () => {
  const h = new Date().getHours();
  if (h >= 6 && h < 14) return 1;
  if (h >= 14 && h < 22) return 2;
  return 3;
};

// const formatTime = (ts) =>
//   new Date(ts).toLocaleTimeString("en-GB", {
//     hour: "2-digit",
//     minute: "2-digit",
//   });


const formatTime = (time) => time; // already formatted (HH:mm)

/* ---------------- COMPONENT ---------------- */

const OverView3 = ({ selectedDate, selectedShift, selectedMachine }) => {
  const [interval, setIntervalValue] = useState("5m");
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  /* ---------------- FETCH ---------------- */
 const getApiDate = (selectedDate) => {
  if (selectedDate === "2026-08-17") {
    return "2026-06-01";
  }

  return selectedDate;
};

  const fetchShiftData = async () => {
    if (!selectedDate || !selectedShift || !selectedMachine) return;

    try {
      setIsLoading(true);
 const apiDate = getApiDate(selectedDate);
      const shift = selectedShift || getCurrentShift();

      const apiUrl = `https://api.hanoch.in/api/live-production-summary/?date=${apiDate}&shift=${shift}&machine_id=${selectedMachine}&interval=${interval}`;

      const res = await fetch(apiUrl);
      if (!res.ok) throw new Error("API error");

      const json = await res.json();
      const isLiveShift =
  isToday(selectedDate) &&
  Number(selectedShift) === getCurrentShift();

let transformed;

if (isLiveShift) {
  const now = new Date();
  const currentTimeStr = now.toTimeString().slice(0, 5);

  transformed =
    json.data?.map((item) => {
      const isFuture = item.time > currentTimeStr;

      return {
        time: item.time,
        good: isFuture ? null : Number(item.good_products || 0),
        bad: isFuture ? null : Number(item.bad_products || 0),
      };
    }) || [];
} else {
  // Past shift / past date → show full data
  transformed =
    json.data?.map((item) => ({
      time: item.time,
      good: Number(item.good_products || 0),
      bad: Number(item.bad_products || 0),
    })) || [];
}


//       const now = new Date();
// const currentTimeStr = now.toTimeString().slice(0, 5); // "HH:mm"

// const transformed =
//   json.data?.map((item) => {
//     const isFuture = item.time > currentTimeStr;

//     return {
//       time: item.time,               // keep all 8 hours
//       good: isFuture ? null : Number(item.good_products || 0),
//       bad: isFuture ? null : Number(item.bad_products || 0),
//     };
//   }) || [];

//       const now = new Date();
// const currentTimeStr = now.toTimeString().slice(0, 5); // "HH:mm"

// const transformed =
//   json.data
//     ?.filter((item) => item.time <= currentTimeStr) // ⬅️ STOP after now
//     .map((item) => ({
//       time: item.time,
//       good: Number(item.good_products || 0),
//       bad: Number(item.bad_products || 0),
//     })) || [];


      // const transformed = json.data?.map((item) => ({
      //   time: item.time,
      //   good: Number(item.good_products || 0),
      //   bad: Number(item.bad_products || 0),
      // })) || [];

      setChartData(transformed);
    } catch (err) {
      console.error(err);
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- EFFECTS ---------------- */

  useEffect(() => {
    fetchShiftData();
  }, [selectedDate, selectedShift, selectedMachine, interval]);

  useEffect(() => {
    let timer;

    const shouldPoll =
      selectedDate &&
      selectedShift &&
      isToday(selectedDate) &&
      Number(selectedShift) === getCurrentShift();

    if (shouldPoll) {
      timer = setInterval(fetchShiftData, 60000);
    }

    return () => timer && clearInterval(timer);
  }, [selectedDate, selectedShift, selectedMachine, interval]);

  /* ---------------- CSV ---------------- */

  const downloadCSV = () => {
    if (!chartData.length) return;

    const rows = [
      ["Time", "Good Parts", "Bad Parts"],
      ...chartData.map((r) => [
        formatTime(r.time),
        r.good,
        r.bad,
      ]),
    ];

    const csv =
      "data:text/csv;charset=utf-8," +
      rows.map((r) => r.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = `overview_${selectedDate}_shift_${selectedShift}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ---------------- UI ---------------- */

  return (
    <Col xl={12} style={{ marginBottom: "1.5rem" }}>
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Overview</h5>

          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm"
              style={{ width: 160 }}
              value={interval}
              onChange={(e) => setIntervalValue(e.target.value)}
            >
              <option value="5m">Per 5 Minutes</option>
              <option value="15m">Per 15 Minutes</option>
              <option value="1h">Per 1 Hour</option>
            </select>

            <Button
              color="primary"
              size="sm"
              onClick={downloadCSV}
              disabled={!chartData.length}
            >
              Download CSV
            </Button>
          </div>
        </CardHeader>

        <CardBody style={{ height: 360 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#45496a36" />
              <XAxis
                dataKey="time"
                tickFormatter={formatTime}
                stroke="#a6b0cf"
              />
              <YAxis
                stroke="#a6b0cf"
                allowDecimals={false}
              />
              <Tooltip
                labelFormatter={formatTime}
                contentStyle={{ backgroundColor: "#1e293b", border: "none" }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="good"
                name="Good Parts"
                stroke="#2894eb"
                dot={true}
                  connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="bad"
                name="Bad Parts"
                stroke="#df4646"
                dot={true}
                  connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardBody>
      </Card>
    </Col>
  );
};

export default OverView3;
